import React from "react";
import { useNavigate } from "react-router-dom";
import HeroCarousel from "../components/HeroCarousel";
import BookCarousel from "../components/BookCarousel";
import { CATEGORIES } from "../mock";
import api from "../api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useCatalog } from "../context/CatalogContext";
import {
  Sparkles, TrendingUp, Clock3, Headphones, BookOpen, Baby, GraduationCap, Rocket,
  Search, Heart, Briefcase, Landmark, Laugh, Fingerprint, FlaskConical, Church,
  Vote, Palette, Dumbbell, Plane, Users2, Coins, Globe2, Sprout, Languages, BookMarked,
} from "lucide-react";

export default function Home() {
  const { t } = useLang();
  const { user } = useAuth();
  const { books: BOOKS } = useCatalog();
  const navigate = useNavigate();
  const [collections, setCollections] = React.useState([]);

  React.useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data || [])).catch(() => {});
  }, []);

  const stats = React.useMemo(() => {
    const rated = BOOKS.filter((b) => b.ratingCount > 0);
    const hours = Math.round(BOOKS.reduce((n, b) => n + (b.duration || 0), 0) / 3600);
    const avg = rated.length ? rated.reduce((n, b) => n + b.rating, 0) / rated.length : 0;
    const round = (n) => (n >= 10 ? `${Math.floor(n / 10) * 10}+` : String(n));
    return [
      { n: String(BOOKS.length), l: BOOKS.length === 1 ? "Audiobook" : "Audiobooks" },
      { n: round(new Set(BOOKS.map((b) => b.narrator).filter(Boolean)).size), l: "Narrators" },
      { n: `${hours}h`, l: "Of listening" },
      { n: avg ? `${avg.toFixed(1)}★` : "New", l: "Average rating" },
    ];
  }, [BOOKS]);

  const featured = BOOKS.filter((b) => b.featured);
  const bestsellers = BOOKS.filter((b) => b.bestseller);
  const newReleases = BOOKS.filter((b) => b.newRelease);
  const recommended = [...BOOKS].sort((a, b) => b.rating - a.rating).slice(0, 10);
  const [continues, setContinues] = React.useState([]);

  React.useEffect(() => {
    if (user) api.get("/me/continue").then((r) => setContinues(r.data || [])).catch(() => {});
    else setContinues([]);
  }, [user]);

  const catIcons = {
    fiction: BookOpen, thriller: Fingerprint, romance: Heart, fantasy: Rocket,
    children: Baby, teen: GraduationCap, selfhelp: Sparkles, biography: Users2,
    business: Briefcase, money: Coins, history: Landmark, comedy: Laugh,
    crime: Search, nonfiction: BookMarked, science: FlaskConical, religion: Church,
    politics: Vote, arts: Palette, sports: Dumbbell, travel: Plane,
    relationships: Heart, education: GraduationCap, classics: BookOpen, lgbtq: Users2,
    homegarden: Sprout, language: Languages, reference: BookMarked, cooking: Sprout,
    nature: Globe2, tech: FlaskConical,
  };

  return (
    <div className="animate-float-up">
      <HeroCarousel />

      {/* stats strip */}
      <div className="border-y border-border bg-card/30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Counted from the catalogue rather than written down. The fixed
              copy claimed 12,000 audiobooks and 800 narrators above a shelf of
              two dozen — the first thing a visitor checks, and the cheapest
              way to lose them. These numbers grow on their own. */}
          {stats.map((s) => (
            <div key={s.l} className="text-center">
              <div className="font-display text-2xl sm:text-3xl font-semibold brand-text">{s.n}</div>
              <div className="text-xs sm:text-sm text-muted-foreground mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      <BookCarousel title={t("section_featured")} books={featured} seeAllTo="/browse" />
      {user && continues.length > 0 && (
        <BookCarousel title={t("continue_listening")} books={continues.map((c) => c.book)} seeAllTo="/library" />
      )}
      <BookCarousel title={t("section_bestsellers")} books={bestsellers} seeAllTo="/browse?sort=rating" />

      {/* curated collections (managed by admin) */}
      {collections.map((col) => (
        <div key={col.id}>
          {col.subtitle && (
            <p className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 -mb-3 pt-2 text-sm text-muted-foreground">{col.subtitle}</p>
          )}
          <BookCarousel title={col.title} books={col.books} />
        </div>
      ))}

      {/* categories */}
      <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-6">{t("section_categories")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CATEGORIES.map((c) => ({ ...c, count: BOOKS.filter((b) => b.category === c.id).length }))
              .sort((a, b) => b.count - a.count)
              .filter((c) => c.count > 0)
              .map((c) => {
                const Icon = catIcons[c.id] || Headphones;
                return (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/browse?cat=${c.id}`)}
                    className="group relative overflow-hidden rounded-2xl p-6 text-left border border-border bg-gradient-to-br from-secondary to-card hover:border-brand-500/50 transition-all card-hover"
                  >
                    <Icon className="w-7 h-7 text-brand-400 mb-8" />
                    <h3 className="font-semibold text-base">{c.en}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {c.count} {t("results")}
                    </p>
                  </button>
                );
              })}
          </div>
        </div>
      </section>

      <BookCarousel title={t("section_new")} books={newReleases} seeAllTo="/browse?sort=newest" />
      <BookCarousel
        title={user ? t("section_because") : t("section_featured")}
        books={recommended}
        seeAllTo="/browse"
      />

      {/* CTA band */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-brand-500/20 bg-gradient-to-br from-secondary via-card to-secondary p-10 sm:p-14 text-center">
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                {"Your story begins the moment you press play"}
              </h2>
              <p className="text-muted-foreground mt-4">
                {"Premium audiobooks narrated by the finest voices. Listen anywhere, anytime."}
              </p>
              <button
                onClick={() => navigate(user ? "/browse" : "/register")}
                className="mt-7 inline-flex items-center gap-2 h-12 px-8 rounded-full bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold transition-colors"
              >
                {user ? t("hero_explore") : t("signup")}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
